import React, { ReactNode, ComponentType } from 'react';
import { ArrowForward, MoreVert } from '@mui/icons-material';
import { cn } from 'src/utils/Functions';

interface ChitActionsProps {
  children?: ReactNode;
  showArrow?: boolean;
  className?: string;
}

export const ChitActions: React.FC<ChitActionsProps> = ({
  children,
  showArrow = false,
  className
}) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {children}
      {showArrow && !children && (
        <ArrowForward className="text-white/30 w-5 h-5" />
      )}
    </div>
  );
};

// Action button component
interface ChitActionButtonProps {
  children: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'success' | 'warning' | 'error';
  size?: 'sm' | 'default';
  icon?: ReactNode | ComponentType<any>;
  className?: string;
}

export const ChitActionButton: React.FC<ChitActionButtonProps> = ({
  children,
  onClick,
  variant = 'default',
  size = 'default',
  icon,
  className
}) => {
  const baseClasses = "bg-transparent border cursor-pointer flex items-center gap-2 transition-colors rounded-md";
  
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    default: "px-3 py-1.5 text-sm"
  };

  const variantClasses = {
    default: "border-white/20 text-white/60 hover:bg-white/10",
    success: "border-green-500/50 text-green-500 hover:bg-green-500/10",
    warning: "border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10", 
    error: "border-red-500/50 text-red-500 hover:bg-red-500/10"
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
    >
      {icon && (
        React.isValidElement(icon) 
          ? React.cloneElement(icon as React.ReactElement<any>, { 
              className: cn("w-3.5 h-3.5", (icon as any).props?.className)
            })
          : React.createElement(icon as ComponentType<{ className?: string }>, { className: "w-3.5 h-3.5" })
      )}
      {children}
    </button>
  );
};

// More actions button (3 dots)
interface ChitMoreActionsProps {
  onClick: () => void;
  className?: string;
}

export const ChitMoreActions: React.FC<ChitMoreActionsProps> = ({
  onClick,
  className
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "text-white/40 bg-transparent border-none cursor-pointer p-2 rounded-md flex items-center hover:bg-white/10 transition-colors",
        className
      )}
    >
      <MoreVert className="w-5 h-5" />
    </button>
  );
};

// Simple arrow indicator
interface ChitArrowProps {
  className?: string;
}

export const ChitArrow: React.FC<ChitArrowProps> = ({ className }) => {
  return (
    <ArrowForward className={cn("text-white/30 w-5 h-5", className)} />
  );
};